import { useEffect, useRef, useState } from 'react';
import { useTranslation, useI18n, appendLocaleParam } from '@infra/i18n';
import { motion } from 'framer-motion';
import * as THREE from 'three';
import NET from 'vanta/dist/vanta.net.min';
import { environment } from '@shared/config';
import { brand as brandPack } from '@brand';
import { Button } from '@shared/ui';
import { ArrowLeft, LogIn, ShieldCheck } from 'lucide-react';

type FullScreenErrorProps = {
  code: '403' | '404';
};

export function FullScreenError({ code }: FullScreenErrorProps) {
  const { t } = useTranslation();
  const i18n = useI18n();

  const [brand, setBrand] = useState<string | null>(null);
  const [brandHighlight, setBrandHighlight] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      try {
        const b = await i18n.tAsync('landing:loading.title');
        const bh = await i18n.tAsync('landing:loading.titleHighlight');
        if (!mounted) return;
        setBrand(b);
        setBrandHighlight(bh);
      } catch {
        // ignore
      }
    })();
    return () => {
      mounted = false;
    };
  }, [i18n]);

  const vantaRef = useRef<HTMLDivElement>(null);

  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const isForbidden = code === '403';

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!vantaRef.current || prefersReducedMotion) return;

    // Polyfill THREE.VertexColors which was removed in newer three.js versions
    // Vanta still tries to access it, causing the "vertexColors has value of undefined" warning.
    const threeProxy = new Proxy(THREE, {
      get: (target: any, prop) => {
        if (prop === 'VertexColors') return true; // Polyfill for modern three.js where vertexColors is a boolean
        return target[prop];
      },
    });

    let effect: { destroy: () => void } | null = null;
    try {
      effect = NET({
        el: vantaRef.current,
        THREE: threeProxy,
        mouseControls: true,
        touchControls: false,
        gyroControls: false,
        minHeight: 200.0,
        minWidth: 200.0,
        scale: 1.0,
        scaleMobile: 1.0,
        points: 12.0,
        maxDistance: 22.0,
        spacing: 18.0,
        // Keep the error-code accent line, but use a very subtle dark Vanta base
        // so the brighter theme gradient can sit above it.
        color: isForbidden ? 0xef4444 : 0x3b82f6,
        backgroundColor: 0x020617,
        showDots: true,
      });
    } catch (error) {
      console.error('Failed to initialize Vanta effect:', error);
    }
    return () => {
      if (effect) effect.destroy();
    };
  }, [prefersReducedMotion, isForbidden]);

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: prefersReducedMotion ? 0.4 : 1.0,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const numberVariants =
    code === '404'
      ? {
          animate: {
            y: [0, -14, 0],
            rotate: [0, 2, -2, 0],
            filter: ['var(--error-glow-1)', 'var(--error-glow-2)', 'var(--error-glow-3)'],
            transition: {
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          },
        }
      : {
          animate: {
            x: [0, -2, 2, -2, 2, 0],
            y: [0, 1, -1, 1, -1, 0],
            filter: [
              'var(--forbidden-glow-1)',
              'var(--forbidden-glow-2)',
              'var(--forbidden-glow-3)',
              'var(--forbidden-glow-4)',
            ],
            transition: {
              duration: 0.4,
              repeat: Infinity,
              repeatDelay: 3,
              ease: 'easeInOut',
            },
          },
        };

  const textShimmer = {
    animate: {
      backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
    },
    transition: {
      duration: 6,
      ease: 'linear',
      repeat: Infinity,
    },
  };

  const titleKey = code === '403' ? 'common:errors.403.title' : 'common:errors.404.title';
  const messageKey = code === '403' ? 'common:errors.403.message' : 'common:errors.404.message';

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-background text-foreground font-sans selection:bg-primary/25">
      {!prefersReducedMotion && (
        <div
          ref={vantaRef}
          className="absolute inset-0 z-0 pointer-events-none opacity-60 mix-blend-screen"
        />
      )}

      {prefersReducedMotion && (
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-background via-background/80 to-background" />
      )}

      {/* Subtle light-theme overlay using the background token. */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-background/0 via-background/70 to-background pointer-events-none" />

      {!prefersReducedMotion && (
        <div
          className={
            'absolute inset-0 z-[2] pointer-events-none opacity-[0.04] bg-[linear-gradient(var(--scanline-dark)_50%,var(--scanline-dark-strong)_50%),linear-gradient(90deg,' +
            (isForbidden
              ? 'var(--error-overlay-1),var(--error-overlay-2),var(--error-overlay-3)'
              : 'var(--info-overlay-1),var(--info-overlay-2),var(--info-overlay-3)') +
            ')] bg-[length:100%_4px,6px_100%]'
          }
        />
      )}

      <main className="relative z-10 flex flex-col min-h-screen p-6 md:p-10">
        <header className="flex-none mb-6 md:mb-0">
          <div className="inline-flex items-center gap-3 px-3 py-2 rounded-full bg-primary/5 border border-primary/10 shadow-sm">
            <div className="bg-primary/10 p-1.5 rounded-lg">
              <ShieldCheck className="w-4 h-4 text-primary" />
            </div>
            <span className="font-semibold text-sm tracking-tight text-foreground uppercase">
              {(brand ?? t('landing:loading.title'))
                .replace(brandHighlight ?? t('landing:loading.titleHighlight'), '')
                .trim()}
              <span className="text-primary">
                {' '}
                {brandHighlight ?? t('landing:loading.titleHighlight')}
              </span>
            </span>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center p-4">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="relative w-full max-w-[560px]"
          >
            {!prefersReducedMotion && (
              <motion.div
                animate={{
                  scale: [1, code === '403' ? 1.15 : 1.1, 1],
                  opacity: [0.2, code === '403' ? 0.45 : 0.55, 0.2],
                }}
                transition={{
                  duration: code === '403' ? 10 : 8,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                className={
                  'absolute rounded-full blur-[100px] ' +
                  (code === '403' ? '-inset-12 bg-tone-negative-solid/15' : '-inset-10 bg-tone-info-solid/15')
                }
              />
            )}

            <div className="relative overflow-hidden rounded-2xl border border-border bg-card/95 backdrop-blur-xl shadow-2xl p-8 md:p-12 text-center">
              <motion.div
                variants={!prefersReducedMotion ? numberVariants : {}}
                animate={!prefersReducedMotion ? 'animate' : undefined}
                className="inline-block"
              >
                <motion.h1
                  className={
                    'text-7xl md:text-8xl lg:text-9xl font-black tracking-tighter mb-4 bg-clip-text text-transparent bg-[length:200%_auto] ' +
                    (code === '403'
                      ? 'bg-gradient-to-r from-tone-negative-bright via-tone-negative-warm to-tone-negative-solid'
                      : 'bg-gradient-to-r from-tone-info-bright via-tone-info-mid to-tone-info-solid')
                  }
                  variants={!prefersReducedMotion ? textShimmer : {}}
                  animate={!prefersReducedMotion ? 'animate' : undefined}
                  transition={!prefersReducedMotion ? textShimmer.transition : undefined}
                >
                  {code}
                </motion.h1>
              </motion.div>

              <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-3 tracking-tight">
                {t(titleKey)}
              </h2>

              <p className="text-muted-foreground text-base md:text-lg mb-8 leading-relaxed max-w-md mx-auto">
                {t(messageKey)}
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                <Button
                  className="w-full sm:w-auto min-w-[150px] h-12 rounded-full shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-all hover:-translate-y-[1px]"
                  size="lg"
                  onClick={() => window.location.assign('/')}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t('common:buttons.back')}
                </Button>

                <Button
                  className="w-full sm:w-auto min-w-[150px] h-12 rounded-full border-muted bg-card hover:bg-background transition-all hover:-translate-y-[1px]"
                  size="lg"
                  variant="outline"
                  onClick={() => window.location.assign(appendLocaleParam(environment.loginUrl))}
                >
                  <LogIn className="mr-2 h-4 w-4" />
                  {t('auth:login.submit')}
                </Button>
              </div>

              <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground border-t border-border pt-5">
                {brandPack.appName} ·{' '}
                {code === '403' ? t('common:messages.warning') : t('common:messages.loadFailed')}
              </p>
            </div>
          </motion.div>
        </div>

        <footer className="flex-none text-center text-muted-foreground text-xs md:text-sm py-4">
          © {new Date().getFullYear()} {brandPack.appName} · {t('common:nav.appTagline')}
        </footer>
      </main>
    </div>
  );
}

export function ForbiddenScreen() {
  return <FullScreenError code="403" />;
}

export function NotFoundScreen() {
  return <FullScreenError code="404" />;
}
